import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import collaborationCopy from '@/i18n/ns/workspace-collaboration';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { AvatarStack, StatusPill } from '@/components/ui/kit';
import { WorkspaceIcon } from '../icons';
import { SourceRow } from './sources-panel';
import { useTimeLabel } from './format';
import type { CheckRowVM, PublishBlockReason, PublishSheetVM } from './types';

// The manual publish sheet: audience, owner line, Checks (warnings never
// block; a failure does), the sources it will freeze, what a publish would
// remove, and the C9 outcome. Agent refreshes never come here. A person who may
// not publish gets "Ask <publishers>", never a button that will be refused. The
// sheet never approves anything: access and removals are decisions made here.

export interface PublishSheetProps {
  vm: PublishSheetVM;
  onPublish?: () => void;
  onFixInChat?: () => void;
  onRefresh?: (checkId: string) => void;
  onReviewRemoval?: () => void;
  onReviewAccess?: () => void;
  onAsk?: () => void;
  onUndo?: () => void;
  onCopyLink?: () => void;
  onDone?: () => void;
  onCancel?: () => void;
}

const BLOCK_KEY: Record<PublishBlockReason, string> = {
  not_authorized: 'publishBlockedNotAuthorized',
  check_failed: 'publishBlockedCheckFailed',
  check_stale: 'publishBlockedCheckStale',
  objects_unverified: 'publishBlockedObjects',
  policy_changed: 'publishBlockedPolicy',
  approval_invalid: 'publishBlockedApproval',
  dependency_unavailable: 'publishBlockedDependency',
};

export function PublishSheet(props: PublishSheetProps) {
  const { vm } = props;
  if (vm.outcome.status === 'published' && vm.done)
    return <PublishDone {...props} done={vm.done} />;
  return <PublishForm {...props} />;
}

function PublishForm({
  vm,
  onPublish,
  onFixInChat,
  onRefresh,
  onReviewRemoval,
  onReviewAccess,
  onAsk,
  onCancel,
}: PublishSheetProps) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const { outcome } = vm;
  const number = String(vm.number);
  const failures = vm.checkRows.filter((row) => row.kind === 'failed');
  const warnings = vm.checkRows.filter((row) => row.kind !== 'failed');
  const removal = vm.removal;
  const needsGrant =
    outcome.status === 'confirmation' &&
    (outcome.reason === 'grant' || outcome.reason === 'external_write');
  // Only an idle sheet, or a blocked outcome the contract marks retryable, may publish.
  const settled = outcome.status === 'idle' || (outcome.status === 'blocked' && outcome.retryable);
  const reviewedRemoval = removal === null || removal.reviewed;
  const canPublish =
    vm.canPublish && vm.check === 'passed' && settled && failures.length === 0 && reviewedRemoval;
  const needsFix = vm.check === 'failed' || vm.check === 'stale' || failures.length > 0;
  const askNames = vm.publishers.length ? vm.publishers.join(', ') : null;
  return (
    <section className="ap-panel" aria-labelledby="ap-publish-title" data-panel="publish">
      <header className="ap-head">
        <h2 id="ap-publish-title">{copy.publishTitle}</h2>
        <p>{formatCopy(copy.publishIntro, { number, audience: vm.audienceLabel })}</p>
        <p>{formatCopy(copy.publishOwnerLine, { owner: vm.ownerLabel })}</p>
      </header>
      <div className="ap-check" data-check={vm.check}>
        <h3>{copy.publishChecksTitle}</h3>
        <p>
          <WorkspaceIcon name={vm.check === 'passed' ? 'check' : 'clock'} size={14} />
          {copy[CHECK_LINE[vm.check]]}
        </p>
        {failures.map((row) => (
          <Callout key={row.id} tone="danger">
            {row.detail ??
              (row.subject
                ? formatCopy(copy.checkFailedRow, { subject: row.subject })
                : copy.checkFailedGeneric)}
          </Callout>
        ))}
        {needsFix && (
          <Button type="button" variant="outline" size="sm" onClick={onFixInChat}>
            {copy.publishFixInChat}
          </Button>
        )}
        {warnings.length > 0 && (
          <div className="ap-warnings">
            <p className="ap-eyebrow">{copy.publishWarnings}</p>
            {warnings.map((row) => (
              <WarningRow key={row.id} row={row} onRefresh={onRefresh} />
            ))}
          </div>
        )}
      </div>
      {vm.sources.length > 0 && (
        <ul className="ap-list" aria-label={copy.sourcesTitle}>
          {vm.sources.map((row) => (
            <SourceRow key={row.id} row={row} />
          ))}
        </ul>
      )}
      {removal && (
        <Callout
          tone="warning"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={onReviewRemoval}>
              {copy.publishReviewRemoval}
            </Button>
          }
        >
          {formatCopy(copy.publishRemovalBody, {
            number,
            what: removal.what,
            count: String(removal.count),
          })}
          {!removal.reviewed && ` ${copy.publishReviewFirst}`}
        </Callout>
      )}
      {needsGrant && (
        <Callout
          tone="warning"
          actions={
            <Button type="button" variant="outline" size="sm" onClick={onReviewAccess}>
              {copy.publishReviewAccess}
            </Button>
          }
        >
          {outcome.status === 'confirmation' && outcome.reason === 'external_write'
            ? copy.publishConfirmWrite
            : copy.publishConfirmGrant}
        </Callout>
      )}
      <Outcome vm={vm} />
      {!vm.canPublish && (
        <p className="ap-note">
          {askNames
            ? formatCopy(copy.publishAskFooter, { publishers: askNames })
            : copy.publishAskNone}
        </p>
      )}
      <div className="ap-actions">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {copy.close}
          </Button>
        )}
        {vm.canPublish ? (
          <Button
            type="button"
            variant={removal ? 'destructive' : 'default'}
            disabled={!canPublish}
            onClick={onPublish}
          >
            {outcome.status === 'publishing'
              ? copy.publishing
              : removal
                ? copy.publishAndRemove
                : formatCopy(copy.publishAction, { number })}
          </Button>
        ) : (
          <Button type="button" onClick={onAsk}>
            {askNames ? formatCopy(copy.publishAsk, { publishers: askNames }) : copy.publishAskNone}
          </Button>
        )}
      </div>
    </section>
  );
}

const CHECK_LINE = {
  passed: 'checkPassed',
  running: 'checkRunning',
  failed: 'checkFailed',
  stale: 'checkStale',
} as const;

function WarningRow({
  row,
  onRefresh,
}: {
  row: CheckRowVM;
  onRefresh?: (checkId: string) => void;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const time = useTimeLabel();
  const subject = row.subject;
  const sentence = !subject
    ? copy.checkWarnUnknownSource
    : row.kind === 'source-stale'
      ? formatCopy(copy.checkWarnStale, { subject, time: time(row.asOf) ?? '' })
      : formatCopy(copy.checkWarnGap, { subject });
  return (
    <div className="ap-warning-row">
      <StatusPill tone="warning">{sentence}</StatusPill>
      {row.canRefresh && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onRefresh?.(row.id)}>
          {copy.checkRefreshFirst}
        </Button>
      )}
    </div>
  );
}

function Outcome({ vm }: { vm: PublishSheetVM }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const { outcome } = vm;
  switch (outcome.status) {
    case 'idle':
    case 'publishing':
    case 'published':
    case 'confirmation':
      return null;
    case 'conflict':
      return <Callout tone="warning">{copy.publishConflict}</Callout>;
    case 'blocked':
      return (
        <Callout tone="danger">{copy[BLOCK_KEY[outcome.reason] as keyof typeof copy]}</Callout>
      );
  }
}

/** The publish moment's content; the motion belongs to DES2 (`data-moment`). */
function PublishDone({
  vm,
  done,
  onUndo,
  onCopyLink,
  onDone,
}: PublishSheetProps & { done: NonNullable<PublishSheetVM['done']> }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const collaboration = useNamespaceCopy(collaborationCopy);
  const number = vm.outcome.status === 'published' ? vm.outcome.number : vm.number;
  const names = done.people.map((p) => p.name);
  const who =
    done.others > 0
      ? formatCopy(collaboration.morePeople, { names: names.join(', '), count: done.others })
      : names.join(', ');
  return (
    <section
      className="ap-panel ap-done"
      aria-labelledby="ap-publish-done-title"
      data-panel="publish"
      data-moment="publish-done"
    >
      <span className="ap-done-check" aria-hidden="true" data-moment-part="check">
        <WorkspaceIcon name="check" size={28} />
      </span>
      <h2 id="ap-publish-done-title" role="status">
        {formatCopy(copy.publishDoneTitle, { number: String(number), audience: vm.audienceLabel })}
      </h2>
      <div data-moment-part="faces">
        <AvatarStack people={done.people} />
      </div>
      <p>{who}</p>
      <p>{copy.publishDoneWhere}</p>
      <div className="ap-actions">
        {done.canUndo && (
          <Button type="button" variant="ghost" onClick={onUndo}>
            {copy.publishUndo}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onCopyLink}>
          {copy.publishCopyLink}
        </Button>
        <Button type="button" onClick={onDone}>
          {copy.publishDone}
        </Button>
      </div>
    </section>
  );
}
